import React, { useState, useEffect, useMemo } from "react";
import { DashboardStatsSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { TransactionApi } from "@/api/transaction.api";
import { MerchantApi } from "@/api/merchant.api";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { MetricsCard } from "@/components/ui/metrics-card";
import { Gift, CreditCard, BarChart2, Star } from "lucide-react";
import { endOfDay, startOfDay, subMonths } from "date-fns";
import { DateRange } from "react-day-picker";
import { DashboardDateRangePicker } from "@/components/ui/dashboard-date-range-picker";
import { toast } from "sonner";
import { useUserInfo } from "@/hooks/use-user-info";
import { isMerchantAdmin } from "@/utils/role.utils";

const getDefaultDateRange = (): DateRange => ({
  from: startOfDay(subMonths(new Date(), 3)),
  to: endOfDay(new Date()),
});

const MERCHANT_PIE_COLORS = [
  "#1e40af",
  "#3b82f6",
  "#93c5fd",
  "#2563eb",
  "#60a5fa",
  "#bfdbfe",
  "#1d4ed8",
  "#6366f1",
];

const STATUS_PIE_COLORS: Record<string, string> = {
  ACCRUAL_SUCCESSFUL: "#22c55e",
  ACCURAL_SUCCESSFUL: "#22c55e",
  ACCURAL_IN_PROGRESS: "#3b82f6",
  ACCRUAL_IN_PROGRESS: "#3b82f6",
  NOT_ACCRUED: "#eab308",
  NOT_ACCRUAL: "#eab308",
  ACCRUAL_FAILED: "#ef4444",
};

function normalizeAccrualStatus(status: string) {
  return String(status || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function formatAccrualStatus(status: string) {
  switch (normalizeAccrualStatus(status)) {
    case "ACCRUAL_SUCCESSFUL":
    case "ACCURAL_SUCCESSFUL":
      return "Success";
    case "ACCRUAL_FAILED":
      return "Failed";
    case "ACCRUAL_IN_PROGRESS":
    case "ACCURAL_IN_PROGRESS":
      return "In Progress";
    case "NOT_ACCRUED":
    case "NOT_ACCRUAL":
      return "Pending";
    default:
      return status;
  }
}

function formatYAxisTick(value: number) {
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + "k";
  }
  return value.toString();
}

const Dashboard = () => {
  const { userInfo } = useUserInfo();
  const merchantAdmin = isMerchantAdmin(
    userInfo?.roleCode ??
      (() => {
        try {
          return JSON.parse(sessionStorage.getItem("userInfo") || "{}")
            ?.roleCode;
        } catch {
          return undefined;
        }
      })(),
  );
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [currency, setCurrency] = useState<any>(null);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getDefaultDateRange,
  );

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    const startDateInMillis = startOfDay(dateRange.from).getTime();
    const endDateInMillis = endOfDay(dateRange.to).getTime();

    async function fetchStats() {
      setLoading(true);
      let clientId: number | null = null;
      try {
        const storedUserInfo = JSON.parse(
          sessionStorage.getItem("userInfo") || "{}",
        );
        if (
          storedUserInfo?.roleCode === "PROGRAM_ADMIN" &&
          storedUserInfo?.clientId
        ) {
          clientId = storedUserInfo.clientId;
        }
        const res = await TransactionApi.getGeneralStats({
          clientId,
          programType: "EARN",
          startDateInMillis,
          endDateInMillis,
        });
        setStats(res?.data?.generalStatsData || null);
        setCurrency(res?.data?.currencyVO || null);
      } catch {
        setStats(null);
        setCurrency(null);
        toast.error("Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, [dateRange]);

  useEffect(() => {
    async function fetchMerchants() {
      if (
        isMerchantAdmin(
          JSON.parse(sessionStorage.getItem("userInfo") || "{}")?.roleCode,
        )
      ) {
        return;
      }
      try {
        const storedUserInfo = JSON.parse(
          sessionStorage.getItem("userInfo") || "{}",
        );
        const res =
          storedUserInfo?.roleCode === "PROGRAM_ADMIN"
            ? await MerchantApi.getMerchantDataUsingProgramId()
            : await MerchantApi.getAllMerchants();
        setMerchants(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setMerchants([]);
      }
    }
    fetchMerchants();
  }, []);

  const currencyCode = currency?.iso3CurrencyCode || "";

  const merchantNameById = useMemo(() => {
    const map = new Map<number, string>();
    merchants.forEach((merchant) => {
      if (merchant?.id != null) {
        map.set(
          merchant.id,
          merchant.merchantName || merchant.name || `Merchant ${merchant.id}`,
        );
      }
    });
    return map;
  }, [merchants]);

  const merchantContributionData = useMemo(
    () =>
      (stats?.merchantContributionVOS || []).map((item: any) => ({
        name:
          merchantNameById.get(item.merchantId) || `Merchant ${item.merchantId}`,
        value: Number(item.merchantContribution) || 0,
        revenue: Number(item.revenue) || 0,
      })),
    [stats?.merchantContributionVOS, merchantNameById],
  );

  const accrualStatusData = useMemo(
    () =>
      (stats?.accrualStatusContributionVOS || []).map((item: any) => ({
        name: formatAccrualStatus(item.status),
        status: normalizeAccrualStatus(item.status),
        value: Number(item.contributionPercentage) || 0,
        count: Number(item.count) || 0,
      })),
    [stats?.accrualStatusContributionVOS],
  );

  const pointsAndCashbackData = useMemo(
    () => [
      {
        name: "Points Accrued",
        value: Number(stats?.totalPointsAccruedBreakdown ?? stats?.totalPointsAccrued) || 0,
      },
      {
        name: "Cashback Amount",
        value:
          Number(
            stats?.cashbackAmountBreakdown ??
              stats?.totalPointsAccruedEquivalentAmount,
          ) || 0,
      },
    ],
    [stats],
  );

  const newVsRepeatedData = useMemo(
    () => [
      {
        name: "New",
        value: Number(stats?.newTransactions) || 0,
      },
      {
        name: "Repeated",
        value: Number(stats?.repeatedTransactions) || 0,
      },
    ],
    [stats],
  );

  return (
    <div className="space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="w-full sm:w-auto sm:min-w-[360px]">
          <DashboardDateRangePicker
            date={dateRange}
            setDate={setDateRange}
            placeholder="Select date"
          />
        </div>
      </div>
      {loading ? (
        <DashboardStatsSkeleton />
      ) : stats ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricsCard
              title={merchantAdmin ? "Total Programs" : "Total Merchants"}
              value={
                merchantAdmin
                  ? (stats.totalPrograms?.toLocaleString() ?? 0)
                  : (stats.totalMerchants?.toLocaleString() ?? 0)
              }
              icon={<Gift className="h-5 w-5" />}
            />
            <MetricsCard
              title="Total Transactions"
              value={stats.totalTransactions?.toLocaleString() ?? 0}
              icon={<CreditCard className="h-5 w-5" />}
            />
            <MetricsCard
              title="Points Accrued"
              value={stats.totalPointsAccrued?.toLocaleString() ?? 0}
              icon={<Star className="h-5 w-5" />}
            />
            <MetricsCard
              title="Revenue"
              value={
                <>
                  {(stats.revenue ?? 0).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                  })}{" "}
                  <span className="text-sm font-normal">{currencyCode}</span>
                </>
              }
              icon={<BarChart2 className="h-5 w-5" />}
            />
          </div>

          {!merchantAdmin && (
            <>
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
                  <h2 className="text-xl font-semibold mb-4">
                    Points Accrued & Cashback
                  </h2>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pointsAndCashbackData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={formatYAxisTick} fontSize={12} />
                        <Tooltip
                          formatter={(val: any, name: any, props: any) => {
                            const label = props?.payload?.name;
                            if (label === "Cashback Amount") {
                              return [
                                `${Number(val).toLocaleString("en-US", {
                                  maximumFractionDigits: 2,
                                })} ${currencyCode}`.trim(),
                                label,
                              ];
                            }
                            return [Number(val).toLocaleString(), label || name];
                          }}
                        />
                        <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    Equivalent amount:{" "}
                    {(
                      Number(stats.totalPointsAccruedEquivalentAmount) || 0
                    ).toLocaleString("en-US", { maximumFractionDigits: 2 })}{" "}
                    {currencyCode}
                  </div>
                </div>

                <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
                  <h2 className="text-xl font-semibold mb-4">
                    New vs Repeated Transactions
                  </h2>
                  <div className="h-[300px] w-full">
                    {newVsRepeatedData.some((item) => item.value > 0) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={newVsRepeatedData}
                            cx="50%"
                            cy="55%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            <Cell fill="#3b82f6" stroke="#fff" strokeWidth={2} />
                            <Cell fill="#22c55e" stroke="#fff" strokeWidth={2} />
                          </Pie>
                          <Tooltip
                            formatter={(val: any) => [
                              Number(val).toLocaleString(),
                              "Transactions",
                            ]}
                          />
                          <Legend verticalAlign="top" align="center" />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-500">
                        No transaction mix data available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
                  <h2 className="text-xl font-semibold mb-4">
                    Top Merchant-wise Contribution
                  </h2>
                  <div className="h-[320px] w-full">
                    {merchantContributionData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={merchantContributionData}
                            cx="50%"
                            cy="55%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {merchantContributionData.map((_, index) => (
                              <Cell
                                key={`merchant-cell-${index}`}
                                fill={
                                  MERCHANT_PIE_COLORS[
                                    index % MERCHANT_PIE_COLORS.length
                                  ]
                                }
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(val: any, _name: any, props: any) => [
                              `${Number(val).toFixed(2)}% (${Number(
                                props?.payload?.revenue || 0,
                              ).toLocaleString("en-US", {
                                maximumFractionDigits: 2,
                              })} ${currencyCode})`.trim(),
                              "Contribution",
                            ]}
                          />
                          <Legend verticalAlign="top" align="center" />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-500">
                        No merchant contribution data available
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
                  <h2 className="text-xl font-semibold mb-4">
                    Accrual Status Distribution
                  </h2>
                  <div className="h-[320px] w-full">
                    {accrualStatusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={accrualStatusData}
                            cx="50%"
                            cy="55%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {accrualStatusData.map(
                              (entry: any, index: number) => (
                                <Cell
                                  key={`status-cell-${index}`}
                                  fill={
                                    STATUS_PIE_COLORS[entry.status] ||
                                    MERCHANT_PIE_COLORS[
                                      index % MERCHANT_PIE_COLORS.length
                                    ]
                                  }
                                  stroke="#fff"
                                  strokeWidth={2}
                                />
                              ),
                            )}
                          </Pie>
                          <Tooltip
                            formatter={(val: any, _name: any, props: any) => [
                              `${Number(val).toFixed(2)}% (${
                                props?.payload?.count ?? 0
                              })`,
                              "Share",
                            ]}
                          />
                          <Legend verticalAlign="top" align="center" />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-500">
                        No accrual status data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center text-gray-500">No data available</div>
      )}
    </div>
  );
};

export default Dashboard;
